import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ThemeContext = createContext(null);

/**
 * Dark mode + High Contrast / Large Text preferences.
 *
 * Source of truth = the authenticated user/employee record.
 * - For admin/tester/viewer (`kind === 'user'`): `dark_mode` only.
 * - For shop-floor employees (`kind === 'employee'`): `dark_mode` + `high_contrast`
 *   (high_contrast is admin-set on the employee record; employees can't toggle it).
 *
 * Falls back to localStorage when logged out so the login page can still
 * render in dark mode if the visitor previously opted in.
 */
export function ThemeProvider({ children }) {
  const { user, kind, refreshUser } = useAuth();

  const darkMode = user && user.dark_mode;
  const highContrast = user && kind === "employee" && user.high_contrast;

  // Apply class hooks on <html> so global CSS in index.css can target them.
  useEffect(() => {
    const root = document.documentElement;
    const stored = localStorage.getItem("jackstone_dark") === "1";
    const wantDark = !!darkMode || (!user && stored);
    root.classList.toggle("dark", wantDark);
    root.classList.toggle("hc-mode", !!highContrast);
  }, [darkMode, highContrast, user]);

  const toggleDarkMode = useCallback(async () => {
    const next = !(user && user.dark_mode);
    if (!user) {
      // Logged-out visitor — persist locally only.
      localStorage.setItem("jackstone_dark", next ? "1" : "0");
      document.documentElement.classList.toggle("dark", next);
      return;
    }
    try {
      const endpoint = kind === "employee" ? "/employee-auth/preferences" : "/auth/preferences";
      const { data } = await api.put(endpoint, { dark_mode: next });
      // Endpoints return the full public user/employee shape.
      refreshUser(data);
    } catch {
      // Best-effort — still flip the class so the user gets feedback.
      document.documentElement.classList.toggle("dark", next);
    }
  }, [user, kind, refreshUser]);

  const value = useMemo(
    () => ({ darkMode, highContrast, toggleDarkMode }),
    [darkMode, highContrast, toggleDarkMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
